import type {
  AssistantInputAction,
  AssistantPresentationModel,
  AssistantPresentationChecklistItem,
  AssistantPresentationMetricItem,
  AssistantPrompt,
} from '@triedge/ai-assistant-ui';
import settingsSpecialistDomainMap from '../../../../../config/settings-intent/ystnj-settings-specialist-domain-map-v1.json';
import settingsMixedModuleCases from '../../../../../config/settings-intent/ystnj-settings-mixed-module-cases-v1.json';
import {
  resolveAssistantRouteFamily,
  type AssistantRouteFamilyId,
} from '../../config/ai-assistant/route-family-registry';
import { routeMatchesAssistantPattern } from '../../config/ai-assistant/route-matching';

type SettingsAuthorityClass =
  | 'settings-route'
  | 'cross-module-setting'
  | 'derived-effect'
  | 'inventory-unresolved'
  | 'legacy';

type SettingsImplementationStatus =
  | 'implemented'
  | 'partial'
  | 'inventory-only'
  | 'deprecated';

type SettingsAuthorityRecord = {
  id: string;
  label: string;
  authorityClass: SettingsAuthorityClass;
  implementationStatus: SettingsImplementationStatus;
  taxonomyGroupIds: string[];
  routePaths: string[];
  boundedContexts: string[];
  seedRequirementIds: string[];
  journeyIds: string[];
  sourceRefs: string[];
};

type SettingsSpecialistCase = {
  id: string;
  label: string;
  prompt: string;
  summary: string;
  badge?: string;
  targetRoute?: string | null;
  mutationMode?: 'ui-write' | 'control-plane-write' | 'read-only' | 'future-wave';
};

type SettingsMixedModuleCaseRecord = {
  id: string;
  label: string;
  prompt: string;
  summary: string;
  badge?: string;
  routePrefixes?: string[];
  authorityIds?: string[];
  taxonomyGroupIds?: string[];
  journeyIds?: string[];
  mutationMode: 'ui-write' | 'control-plane-write' | 'read-only' | 'future-wave';
};

type SettingsMixedModuleRouteFamilyRecord = {
  routeFamilyId: AssistantRouteFamilyId;
  sectionLabel: string;
  cases: SettingsMixedModuleCaseRecord[];
};

export type AssistantSettingsSpecialistOwnerRoute = {
  id: string;
  label: string;
  href: string;
  summary: string;
};

export type AssistantSettingsSpecialistSnapshot = {
  enabled: boolean;
  routeFamilyId: AssistantRouteFamilyId;
  routeLabel: string;
  title: string;
  browserSectionLabel: string;
  authorities: SettingsAuthorityRecord[];
  ownerRoutes: AssistantSettingsSpecialistOwnerRoute[];
  presetCases: SettingsSpecialistCase[];
  promptItems: AssistantPrompt[];
  inputActions: AssistantInputAction[];
  metricItems: AssistantPresentationMetricItem[];
  checklistItems: AssistantPresentationChecklistItem[];
};

export type AssistantSettingsSpecialistGuide = {
  title: string;
  userPrompt: string;
  assistantSummary: string;
  presentation: AssistantPresentationModel;
};

const AUTHORITIES = (
  (settingsSpecialistDomainMap as {
    authorities?: SettingsAuthorityRecord[];
  }).authorities ?? []
).filter(Boolean);

const MIXED_MODULE_CASES = settingsMixedModuleCases as {
  globalCases?: SettingsMixedModuleCaseRecord[];
  routeFamilies?: SettingsMixedModuleRouteFamilyRecord[];
};

const ROUTE_FAMILY_FALLBACKS: Partial<Record<AssistantRouteFamilyId, string[]>> = {
  settings: [
    'settings-general',
    'settings-users',
    'settings-admissions',
    'settings-financial-aid',
  ],
  tuition: ['school-tuition-settings'],
  donations: ['school-donations-settings', 'tip-a-teacher-settings', 'parnes-hayom-settings'],
  finance: ['financial-ops-settings'],
  'students-families': ['students-families-settings'],
  'parent-portal': ['parent-portal-settings-projection'],
};

const remapDisabledSettingsPath = (value: string): string => {
  if (
    value === '/super-administrator/control-plane/integrations'
    || value.startsWith('/super-administrator/control-plane/assistant-workflows/')
  ) {
    return '/school/settings/general';
  }
  return value;
};

const normalizePathname = (value: string | null | undefined): string => {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return '/';
  }
  const withLeadingSlash = raw.startsWith('/') ? raw : `/${raw}`;
  const withoutQuery = withLeadingSlash.split('?')[0] ?? withLeadingSlash;
  const collapsed = withoutQuery.replace(/\/{2,}/g, '/').toLowerCase();
  const normalized = collapsed.length > 1 && collapsed.endsWith('/') ? collapsed.slice(0, -1) : collapsed;
  return remapDisabledSettingsPath(normalized);
};

const normalizeRoutePattern = (value: string): string => {
  const normalized = normalizePathname(value);
  const segments = normalized.split('/').filter(Boolean);
  const concreteSegments: string[] = [];
  for (const segment of segments) {
    if (segment.startsWith(':')) {
      break;
    }
    concreteSegments.push(segment);
  }
  return concreteSegments.length > 0 ? `/${concreteSegments.join('/')}` : normalized;
};

const routeMatchesPattern = (pathname: string, routePattern: string): boolean => {
  return routeMatchesAssistantPattern(pathname, routePattern);
};

const dedupeByKey = <T,>(items: T[], getKey: (item: T) => string): T[] => {
  const seen = new Set<string>();
  const deduped: T[] = [];
  for (const item of items) {
    const key = getKey(item);
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    deduped.push(item);
  }
  return deduped;
};

const titleCase = (value: string): string => value
  .split(/[\s-]+/g)
  .filter(Boolean)
  .map((token) => `${token.charAt(0).toUpperCase()}${token.slice(1)}`)
  .join(' ');

const buildAuthorityBadge = (authority: SettingsAuthorityRecord): string => {
  if (authority.authorityClass === 'settings-route') {
    return 'Route-owned';
  }
  if (authority.authorityClass === 'derived-effect') {
    return 'Projection';
  }
  return 'Cross-module';
};

const buildAuthoritySummary = (authority: SettingsAuthorityRecord): string => {
  const taxonomy = authority.taxonomyGroupIds.length > 0
    ? authority.taxonomyGroupIds.map((groupId) => titleCase(groupId.replace(/-/g, ' '))).slice(0, 2).join(' • ')
    : 'Settings authority';
  return `${buildAuthorityBadge(authority)} • ${taxonomy}`;
};

const resolveAuthorityRoute = (
  authority: SettingsAuthorityRecord,
  pathname: string,
): string | null => {
  const matchingRoute = authority.routePaths.find((routePath) => routeMatchesPattern(pathname, routePath));
  if (matchingRoute) {
    return normalizeRoutePattern(matchingRoute);
  }
  const concreteRoute = authority.routePaths.find((routePath) => !routePath.includes('/:'));
  if (concreteRoute) {
    return normalizeRoutePattern(concreteRoute);
  }
  const sameFamilyCandidate = authority.routePaths.find((routePath) => {
    const routeFamily = resolveAssistantRouteFamily(routePath);
    return routeFamily.id === resolveAssistantRouteFamily(pathname).id;
  });
  return sameFamilyCandidate ? normalizeRoutePattern(sameFamilyCandidate) : null;
};

const resolveRelevantAuthorities = (pathname: string): SettingsAuthorityRecord[] => {
  const routeFamily = resolveAssistantRouteFamily(pathname);
  const directMatches = AUTHORITIES.filter((authority) => (
    authority.routePaths.some((routePath) => routeMatchesPattern(pathname, routePath))
  ));
  if (directMatches.length > 0) {
    return directMatches;
  }
  const fallbackAuthorityIds = ROUTE_FAMILY_FALLBACKS[routeFamily.id] ?? [];
  return AUTHORITIES.filter((authority) => fallbackAuthorityIds.includes(authority.id));
};

const resolveMixedRouteFamilyRecord = (
  routeFamilyId: AssistantRouteFamilyId,
): SettingsMixedModuleRouteFamilyRecord | null => (
  (MIXED_MODULE_CASES.routeFamilies ?? []).find((record) => record.routeFamilyId === routeFamilyId) ?? null
);

const buildOwnerRoutes = (
  authorities: SettingsAuthorityRecord[],
  pathname: string,
): AssistantSettingsSpecialistOwnerRoute[] => dedupeByKey(
  authorities.flatMap((authority) => {
    const href = resolveAuthorityRoute(authority, pathname);
    if (!href) {
      return [];
    }
    return [{
      id: authority.id,
      label: authority.label,
      href,
      summary: buildAuthoritySummary(authority),
    }];
  }),
  (item) => item.href,
).slice(0, 4);

const caseMatchesRoute = (
  caseRecord: SettingsMixedModuleCaseRecord,
  pathname: string,
): boolean => {
  if (!caseRecord.routePrefixes || caseRecord.routePrefixes.length === 0) {
    return true;
  }
  return caseRecord.routePrefixes.some((routePrefix) => routeMatchesPattern(pathname, routePrefix));
};

const buildSpecialistCases = (
  routeFamilyId: AssistantRouteFamilyId,
  authorities: SettingsAuthorityRecord[],
  pathname: string,
): SettingsSpecialistCase[] => {
  const taxonomySet = new Set(authorities.flatMap((authority) => authority.taxonomyGroupIds));
  const authorityIdSet = new Set(authorities.map((authority) => authority.id));
  const topRoute = buildOwnerRoutes(authorities, pathname)[0]?.href ?? normalizePathname(pathname);
  const routeFamilyRecord = resolveMixedRouteFamilyRecord(routeFamilyId);
  const cases = [
    ...((MIXED_MODULE_CASES.globalCases ?? []) as SettingsMixedModuleCaseRecord[]),
    ...(routeFamilyRecord?.cases ?? []),
  ]
    .filter((entry) => {
      if (!caseMatchesRoute(entry, pathname)) {
        return false;
      }
      if (entry.authorityIds?.some((authorityId) => authorityIdSet.has(authorityId))) {
        return true;
      }
      if (entry.taxonomyGroupIds?.some((taxonomyGroupId) => taxonomySet.has(taxonomyGroupId))) {
        return true;
      }
      return !entry.taxonomyGroupIds?.length && !entry.authorityIds?.length;
    })
    .map((entry) => ({
      id: entry.id,
      label: entry.label,
      prompt: entry.prompt,
      summary: entry.summary,
      badge: entry.badge,
      targetRoute: topRoute,
      mutationMode: entry.mutationMode,
    }));
  return dedupeByKey(cases, (entry) => entry.id).slice(0, 6);
};

const buildMetricItems = (authorities: SettingsAuthorityRecord[]): AssistantPresentationMetricItem[] => {
  const journeyCount = new Set(authorities.flatMap((authority) => authority.journeyIds)).size;
  const seedCount = new Set(authorities.flatMap((authority) => authority.seedRequirementIds)).size;
  const contextCount = new Set(authorities.flatMap((authority) => authority.boundedContexts)).size;
  const partialCount = authorities.filter((authority) => authority.implementationStatus !== 'implemented').length;
  const noExplicitOwner = authorities.length === 0;
  return [
    {
      id: 'authorities',
      label: 'Authorities',
      value: String(authorities.length),
      detail: noExplicitOwner ? 'No explicit settings owner is modeled for this route family yet.' : 'Matched owner surfaces for this route',
      tone: noExplicitOwner ? 'warning' : 'info',
    },
    {
      id: 'journeys',
      label: 'Journeys',
      value: String(journeyCount),
      detail: 'Linked readiness journeys',
      tone: journeyCount > 0 ? 'success' : 'neutral',
    },
    {
      id: 'seed-requirements',
      label: 'Seed refs',
      value: String(seedCount),
      detail: 'Seed requirement ids attached',
      tone: seedCount > 0 ? 'success' : 'warning',
    },
    {
      id: 'coverage',
      label: 'Coverage',
      value: noExplicitOwner ? 'Boundary' : partialCount > 0 ? `${partialCount} partial` : 'Ready',
      detail: noExplicitOwner ? 'This route currently relies on explicit boundary tracing instead of a first-class settings owner.' : partialCount > 0 ? 'Some linked settings surfaces remain partial.' : `${contextCount} bounded contexts linked`,
      tone: noExplicitOwner || partialCount > 0 ? 'warning' : 'success',
    },
  ];
};

const buildChecklistItems = (authorities: SettingsAuthorityRecord[]): AssistantPresentationChecklistItem[] => {
  const items: AssistantPresentationChecklistItem[] = [];
  if (authorities.length === 0) {
    items.push({
      id: 'boundary-trace',
      label: 'Keep the settings overlap explicit',
      detail: 'This route family should trace what is configured elsewhere rather than implying a hidden settings owner.',
      status: 'watch',
    });
  }
  const partialAuthorities = authorities.filter((authority) => authority.implementationStatus !== 'implemented');
  if (partialAuthorities.length > 0) {
    items.push({
      id: 'partial-authorities',
      label: 'Review partial settings coverage',
      detail: partialAuthorities.map((authority) => authority.label).slice(0, 2).join(' • '),
      status: 'watch',
    });
  }
  if (authorities.some((authority) => authority.authorityClass === 'cross-module-setting')) {
    items.push({
      id: 'cross-module-route-check',
      label: 'Check downstream owner routes',
      detail: 'This setting family spans routes outside /school/settings and should be verified at the owning surface.',
      status: 'todo',
    });
  }
  if (authorities.some((authority) => authority.authorityClass === 'derived-effect')) {
    items.push({
      id: 'projection-check',
      label: 'Verify projection surfaces',
      detail: 'Projection-only settings should be checked where parents or public users actually see the effect.',
      status: 'todo',
    });
  }
  if (authorities.some((authority) => authority.taxonomyGroupIds.includes('integrations-sync-export'))) {
    items.push({
      id: 'integration-health',
      label: 'Confirm OAuth and export health',
      detail: 'Integrations, spreadsheet bridges, and payment control-plane status should be checked before relying on generated artifacts.',
      status: 'todo',
    });
  }
  if (authorities.some((authority) => authority.taxonomyGroupIds.includes('identity-access'))) {
    items.push({
      id: 'identity-access-audit',
      label: 'Audit invites, roles, and signers',
      detail: 'Access-visible settings changes should be checked against active users and signer coverage.',
      status: 'ready',
    });
  }
  return dedupeByKey(items, (item) => item.id).slice(0, 4);
};

const buildPromptItems = (
  cases: SettingsSpecialistCase[],
  pathname: string,
): AssistantPrompt[] => cases.map((entry) => ({
  id: `settings-specialist-${entry.id}`,
  label: entry.label,
  badge: entry.badge ?? 'Preset',
  isDynamic: true,
  interactionMode: 'chat-route-start',
  provenance: {
    capabilityId: `settings-specialist:${entry.id}`,
    sourceOrigin: 'settings-specialist',
    scenarioKey: entry.id,
    method: 'settings.specialist.review',
    likelySources: [
      'routeFamilyContext',
      'semanticRouteCatalog',
    ],
    routePrefixes: [normalizePathname(pathname)],
  },
}));

const buildInputActions = (
  cases: SettingsSpecialistCase[],
  ownerRoutes: AssistantSettingsSpecialistOwnerRoute[],
): AssistantInputAction[] => {
  const actions: AssistantInputAction[] = [];
  if (cases.length > 0) {
    actions.push({
      id: 'settings-specialist-case',
      label: 'Run settings review',
      description: 'Start a curated settings-specialist review for the current route.',
      type: 'prompt',
      input: {
        type: 'select',
        placeholder: 'Choose a review case',
        options: cases.map((entry) => ({
          label: entry.label,
          value: entry.prompt,
        })),
      },
      promptTemplate: '{{value}}',
      actionLabel: 'Ask assistant',
    });
  }
  if (ownerRoutes.length > 0) {
    actions.push({
      id: 'settings-specialist-owner-route',
      label: 'Open owner route',
      description: 'Route this review to the surface that actually owns the setting.',
      type: 'navigate',
      input: {
        type: 'select',
        placeholder: 'Choose an owner surface',
        options: ownerRoutes.map((entry) => ({
          label: entry.label,
          value: entry.href,
        })),
      },
      promptTemplate:
        'Open [{{label}}]({{value}}) and explain which settings state is actually owned there, what changes propagate downstream, and what should be verified next.',
      actionLabel: 'Open route',
    });
  }
  return actions;
};

const buildCaseChecklistItems = (
  caseRecord: SettingsSpecialistCase,
  snapshot: AssistantSettingsSpecialistSnapshot,
): AssistantPresentationChecklistItem[] => {
  const mode = caseRecord.mutationMode ?? 'read-only';
  const items: AssistantPresentationChecklistItem[] = [];
  if (mode === 'ui-write') {
    items.push(
      {
        id: `${caseRecord.id}-open-owner`,
        label: 'Open the owner route and apply the real UI change',
        detail: 'Use the route-owned surface instead of editing a shadow projection.',
        status: 'todo',
      },
      {
        id: `${caseRecord.id}-reload-verify`,
        label: 'Reload and confirm the persisted state',
        detail: 'A successful UI write should survive a route refresh and remain visible on the owner surface.',
        status: 'todo',
      },
    );
  } else if (mode === 'control-plane-write') {
    items.push(
      {
        id: `${caseRecord.id}-settings-start`,
        label: 'Start from school settings',
        detail: 'Use the remaining school settings entry point before validating downstream payment surfaces.',
        status: 'todo',
      },
      {
        id: `${caseRecord.id}-downstream-verify`,
        label: 'Verify school/admin/parent handoff surfaces',
        detail: 'Check tuition or parent payment routes after the control-plane write, not before.',
        status: 'todo',
      },
    );
  } else {
    items.push({
      id: `${caseRecord.id}-boundary-review`,
      label: 'Keep the overlap explicit',
      detail: 'Document what is owned here, what is configured elsewhere, and any unmodeled settings boundary.',
      status: 'watch',
    });
  }
  return dedupeByKey([
    ...items,
    ...snapshot.checklistItems,
  ], (item) => item.id).slice(0, 5);
};

export const buildAssistantSettingsSpecialistGuide = (
  snapshot: AssistantSettingsSpecialistSnapshot,
  caseId: string,
): AssistantSettingsSpecialistGuide | null => {
  const caseRecord = snapshot.presetCases.find((entry) => entry.id === caseId);
  if (!caseRecord) {
    return null;
  }

  const assistantSummary = `${caseRecord.label}. ${caseRecord.summary}`;
  return {
    title: caseRecord.label,
    userPrompt: caseRecord.prompt,
    assistantSummary,
    presentation: {
      version: '1.0.0',
      module: 'settings',
      routeFamily: snapshot.routeFamilyId,
      sections: [
        {
          type: 'summary',
          text: assistantSummary,
        },
        {
          type: 'metric_grid',
          title: 'Settings snapshot',
          items: snapshot.metricItems,
        },
        {
          type: 'result_cards',
          title: snapshot.browserSectionLabel,
          cards: snapshot.ownerRoutes.map((route, index) => ({
            id: `${caseRecord.id}-owner-route-${index + 1}`,
            label: route.label,
            eyebrow: 'Owner route',
            badge: 'Route',
            meta: snapshot.routeLabel,
            preview: route.summary,
            primaryAction: {
              label: 'Open owner route',
              href: route.href,
            },
            detailRows: [
              { label: 'Route', value: route.href },
              { label: 'Summary', value: route.summary },
            ],
          })),
        },
        {
          type: 'checklist',
          title: 'Next checks',
          items: buildCaseChecklistItems(caseRecord, snapshot),
        },
        {
          type: 'diagnostics',
          diagnostics: {
            title: 'Settings specialist guide',
            summary: caseRecord.prompt,
            explanation: snapshot.authorities.length === 0
              ? 'No explicit settings owner is modeled for this route family yet, so this guide is intentionally framed as a boundary trace.'
              : `Owner authorities: ${snapshot.authorities.map((authority) => authority.label).join(' • ')}`,
            operatorGuidance: caseRecord.mutationMode === 'control-plane-write'
              ? 'Use the control-plane write first, then verify the downstream school, administrator, and parent payment surfaces.'
              : null,
            targetRoute: snapshot.ownerRoutes[0]?.href ?? null,
            defaultOpen: false,
          },
        },
      ],
    },
  };
};

export const resolveAssistantSettingsSpecialistSnapshot = (
  pathname: string | null | undefined,
): AssistantSettingsSpecialistSnapshot | null => {
  const normalizedPath = normalizePathname(pathname);
  const routeFamily = resolveAssistantRouteFamily(normalizedPath);
  const authorities = resolveRelevantAuthorities(normalizedPath);
  const routeFamilyRecord = resolveMixedRouteFamilyRecord(routeFamily.id);
  if (authorities.length === 0 && !routeFamilyRecord) {
    return null;
  }
  const ownerRoutes = buildOwnerRoutes(authorities, normalizedPath);
  const presetCases = buildSpecialistCases(routeFamily.id, authorities, normalizedPath);
  return {
    enabled: true,
    routeFamilyId: routeFamily.id,
    routeLabel: routeFamily.label,
    title: `${routeFamily.label} settings specialist`,
    browserSectionLabel: routeFamilyRecord?.sectionLabel ?? 'Settings specialist reviews',
    authorities,
    ownerRoutes,
    presetCases,
    promptItems: buildPromptItems(presetCases, normalizedPath),
    inputActions: buildInputActions(presetCases, ownerRoutes),
    metricItems: buildMetricItems(authorities),
    checklistItems: buildChecklistItems(authorities),
  };
};
